import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Trash2 } from 'lucide-react';
import { aiAssistantApi } from '../services/aiAssistantApi';

type AdminUser = { employeeId: number; name: string; chatCount: number; lastConversationAt: string };
type AdminChat = { id: number; title: string; updatedAt: string };
type AdminMessage = { id: number; role: string; content: string; createdAt: string };

function when(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString();
}

const AskStafflyAdmin: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [chats, setChats] = useState<AdminChat[]>([]);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [chatTitle, setChatTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [downloadMenuId, setDownloadMenuId] = useState<number | 'header' | null>(null);
  const [executions, setExecutions] = useState<Array<{ proposalId: number; title: string; status: string; attempts: Array<{ executionKey: string; status: string; error?: string | null }> }>>([]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      setUsers(await aiAssistantApi.adminUsers());
      setExecutions(await aiAssistantApi.openExecutions().catch(() => []));
      setError(null);
    } catch {
      setError('Could not load Ask Staffly conversations.');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const openUser = async (employeeId: number) => {
    setSelectedUserId(employeeId);
    setSelectedChatId(null);
    setMessages([]);
    setChatTitle('');
    setDownloadMenuId(null);
    try {
      setChats(await aiAssistantApi.adminUserChats(employeeId));
      setError(null);
    } catch {
      setChats([]);
      setError('Could not load this user’s chats.');
    }
  };

  const openChat = async (chat: AdminChat) => {
    setSelectedChatId(chat.id);
    setChatTitle(chat.title);
    setDownloadMenuId(null);
    try {
      const thread = await aiAssistantApi.adminChat(chat.id);
      setChatTitle(thread.title);
      setMessages(thread.messages);
      setError(null);
    } catch {
      setMessages([]);
      setError('Could not open that chat.');
    }
  };

  const downloadChat = async (id: number, format: 'xlsx' | 'pdf') => {
    setDownloadMenuId(null);
    try {
      await aiAssistantApi.downloadAdminChat(id, format);
      setError(null);
    } catch {
      setError('Could not download that chat.');
    }
  };

  const deleteChat = async (id: number) => {
    if (!window.confirm('Delete this chat?')) return;
    await aiAssistantApi.deleteChat(id);
    setChats((rows) => rows.filter((row) => row.id !== id));
    if (selectedChatId === id) {
      setSelectedChatId(null);
      setMessages([]);
      setChatTitle('');
    }
    const user = users.find((row) => row.employeeId === selectedUserId);
    if (user && user.chatCount <= 1) {
      setUsers((rows) => rows.filter((row) => row.employeeId !== selectedUserId));
      setSelectedUserId(null);
      setChats([]);
    } else {
      setUsers((rows) => rows.map((row) => (
        row.employeeId === selectedUserId ? { ...row, chatCount: Math.max(0, row.chatCount - 1) } : row
      )));
    }
  };

  const selectedUser = users.find((row) => row.employeeId === selectedUserId);

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-white">
      <aside className="flex w-72 shrink-0 flex-col border-r border-neutral-200 bg-neutral-50">
        <div className="border-b border-neutral-200 px-4 py-3">
          <h1 className="text-base font-semibold text-neutral-900">Ask Staffly Admin</h1>
          <p className="mt-0.5 text-xs text-neutral-500">Users with conversations, newest first</p>
        </div>
        {executions.length > 0 && (
          <div className="border-b border-neutral-200 px-4 py-3">
            <p className="text-xs font-medium text-neutral-800">Uncertain executions</p>
            <ul className="mt-2 space-y-2">
              {executions.map((row) => (
                <li key={row.proposalId} className="text-[11px] text-neutral-600">
                  <span className="font-medium text-neutral-800">{row.title}</span>
                  {' '}#{row.proposalId} · {row.status}
                  {row.attempts[0] && <span className="block break-all">Key {row.attempts[0].executionKey}</span>}
                  {row.attempts[0]?.error && <span className="block">{row.attempts[0].error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {loadingUsers && <p className="px-2 py-3 text-xs text-neutral-500">Loading…</p>}
          {!loadingUsers && users.length === 0 && <p className="px-2 py-3 text-xs text-neutral-500">No conversations yet.</p>}
          {users.map((user) => (
            <button
              key={user.employeeId}
              type="button"
              onClick={() => void openUser(user.employeeId)}
              className={`mb-1 w-full rounded-lg px-3 py-2 text-left ${
                user.employeeId === selectedUserId ? 'bg-white shadow-sm' : 'hover:bg-neutral-200/70'
              }`}
            >
              <span className="block truncate text-sm font-medium text-neutral-900">{user.name}</span>
              <span className="mt-0.5 block text-[11px] text-neutral-500">
                {when(user.lastConversationAt)} · {user.chatCount} {user.chatCount === 1 ? 'chat' : 'chats'}
              </span>
            </button>
          ))}
        </div>
      </aside>

      <aside className="flex w-72 shrink-0 flex-col border-r border-neutral-200">
        <div className="border-b border-neutral-200 px-4 py-3">
          <p className="truncate text-sm font-semibold text-neutral-900">{selectedUser?.name || 'Chats'}</p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {selectedUserId == null && <p className="px-2 py-3 text-xs text-neutral-500">Select a user.</p>}
          {selectedUserId != null && chats.length === 0 && <p className="px-2 py-3 text-xs text-neutral-500">No chats left.</p>}
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`mb-1 flex items-center gap-1 rounded-lg px-2 py-2 ${
                chat.id === selectedChatId ? 'bg-neutral-100' : 'hover:bg-neutral-50'
              }`}
            >
              <button type="button" onClick={() => void openChat(chat)} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-sm text-neutral-800">{chat.title}</span>
                <span className="block text-[11px] text-neutral-400">{when(chat.updatedAt)}</span>
              </button>
              <div className="relative">
                <button
                  type="button"
                  title="Download"
                  aria-label="Download"
                  onClick={() => setDownloadMenuId((open) => (open === chat.id ? null : chat.id))}
                  className="rounded-md p-1.5 text-neutral-600 hover:bg-neutral-200/70"
                >
                  <Download className="h-4 w-4" />
                </button>
                {downloadMenuId === chat.id && (
                  <div className="absolute right-0 z-10 mt-1 w-36 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => void downloadChat(chat.id, 'xlsx')}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadChat(chat.id, 'pdf')}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileText className="h-4 w-4" />
                      PDF
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                title="Delete"
                aria-label="Delete"
                onClick={() => void deleteChat(chat.id)}
                className="rounded-md p-1.5 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <h2 className="truncate text-lg font-semibold text-neutral-900">{chatTitle || 'Conversation'}</h2>
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <button
                type="button"
                disabled={selectedChatId == null}
                onClick={() => setDownloadMenuId((open) => (open === 'header' ? null : 'header'))}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
              {downloadMenuId === 'header' && selectedChatId != null && (
                <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => void downloadChat(selectedChatId, 'xlsx')}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={() => void downloadChat(selectedChatId, 'pdf')}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={selectedChatId == null}
              onClick={() => selectedChatId != null && void deleteChat(selectedChatId)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </header>
        {error && <p className="mx-4 mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{error}</p>}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {selectedChatId == null && <p className="text-sm text-neutral-500">Open a chat to read it. Questions cannot be asked here.</p>}
          {messages.map((message) => (
            <div key={message.id} className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
              {message.role === 'user' && when(message.createdAt) && (
                <p className="mb-1 px-1 text-[11px] text-neutral-400">{when(message.createdAt)}</p>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  message.role === 'user' ? 'rounded-br-md bg-blue-600 text-white' : 'rounded-bl-md bg-neutral-100 text-neutral-800'
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AskStafflyAdmin;
